import React from 'react';
import { Home } from 'lucide-react';

export const SimpleTrackingView = ({ matchData, gameState, recordScore, abandonMatch }) => {
  if (!matchData || !gameState) return null;
  const { player1, player2, player1RaceTo, player2RaceTo } = matchData;

  const handleScoreUpdate = (player, delta) => {
    recordScore(player, delta);
    
    // Add a simple animation effect
    const scoreEl = document.getElementById(`score-${player}`);
    if (scoreEl) {
      scoreEl.classList.add('scale-125');
      setTimeout(() => scoreEl.classList.remove('scale-125'), 300);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-900">
      {/* Header - matching your app's header style */}
      <header className="bg-slate-800/80 p-3 flex justify-between items-center border-b border-slate-700">
        <h2 className="font-bold text-white">{matchData.gameType}</h2>
        <button 
          onClick={abandonMatch} 
          className="p-2 rounded-full hover:bg-slate-700 transition-colors"
          title="Forfeit Match"
        >
          <Home className="w-5 h-5 text-slate-400" />
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 rounded-xl p-6 sm:p-10 border border-slate-700 max-w-md w-full">
          {/* Match info */}
          <div className="text-center mb-8">
            <div className="text-sm uppercase tracking-widest text-slate-400 mb-3">Match</div>
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-2xl font-bold text-white">{player1}</span>
              <span className="text-lg text-slate-500 font-light">vs</span>
              <span className="text-2xl font-bold text-white">{player2}</span>
            </div>
            
            {/* Scores */}
            <div className="flex items-center justify-center gap-8 mb-5">
              <div 
                id="score-player1" 
                className="text-6xl font-extrabold text-white transition-transform duration-300"
              >
                {gameState.player1Score}
              </div>
              <div className="text-4xl text-slate-600 font-extralight">-</div>
              <div 
                id="score-player2" 
                className="text-6xl font-extrabold text-white transition-transform duration-300"
              >
                {gameState.player2Score}
              </div>
            </div>
            
            {/* Race info */}
            <div className="bg-slate-700/50 rounded-lg px-4 py-2 inline-block">
              <span className="text-sm font-medium text-slate-300">
                Race to {player1RaceTo} - {player2RaceTo}
              </span>
            </div>
          </div>

          {/* Control buttons */}
          {!gameState.winner && (
            <div className="grid grid-cols-2 gap-12">
              {/* Player 1 controls */}
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => handleScoreUpdate('player1', 1)}
                  className="w-14 h-14 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 transition-all duration-200 active:scale-95 shadow-lg"
                >
                  <span className="text-2xl font-bold">+</span>
                </button>
                <button
                  onClick={() => handleScoreUpdate('player1', -1)}
                  className="w-14 h-14 rounded-full bg-slate-700 text-slate-300 border-2 border-slate-600 flex items-center justify-center hover:bg-slate-600 transition-all duration-200 active:scale-95"
                >
                  <span className="text-2xl font-bold">−</span>
                </button>
              </div>

              {/* Player 2 controls */}
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => handleScoreUpdate('player2', 1)}
                  className="w-14 h-14 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 transition-all duration-200 active:scale-95 shadow-lg"
                >
                  <span className="text-2xl font-bold">+</span>
                </button>
                <button
                  onClick={() => handleScoreUpdate('player2', -1)}
                  className="w-14 h-14 rounded-full bg-slate-700 text-slate-300 border-2 border-slate-600 flex items-center justify-center hover:bg-slate-600 transition-all duration-200 active:scale-95"
                >
                  <span className="text-2xl font-bold">−</span>
                </button>
              </div>
            </div>
          )}

          {/* Winner display */}
          {gameState.winner && (
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400 mb-2">
                WINNER!
              </div>
              <div className="text-2xl font-semibold text-white">
                {gameState.winner === 'player1' ? player1 : player2} wins!
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};